/**
 * LOT 1 / T1 — sync ticket repris avant encaissement (POS-L0-D1).
 * Fonctions pures : payload PUT + garde paiement + hydratation panier.
 */

import {
  parseOptionsPayload,
  formatOptionsSummary,
  buildLineKey,
} from './caisseProduct.js';

/**
 * Construit le body PUT /caisse/ticket/:id depuis le panier UI.
 * @param {Array} panier
 * @param {(ligne: object) => object} serializeProduit
 * @param {number|null|undefined} expectedPosRevision — L1-T4 obligatoire côté API
 */
export function buildModifierTicketPayload(panier, serializeProduit, expectedPosRevision) {
  const produits = (panier || [])
    .filter((l) => l && l.lineType !== 'prestation')
    .map((l) => serializeProduit(l));
  const body = { produits };
  if (expectedPosRevision != null) {
    body.expectedPosRevision = expectedPosRevision;
  }
  return body;
}

/**
 * Détecte un conflit de révision multi-terminaux (HTTP 409).
 */
export function isTicketRevisionConflict(err) {
  const code = err?.code || err?.data?.error?.code || err?.data?.code;
  return code === 'TICKET_REVISION_CONFLICT' || err?.data?.conflict === true;
}

/**
 * Message + ticket serveur pour reprise contrôlée après conflit.
 */
export function resolveTicketRevisionConflictUi(err) {
  const data = err?.data || {};
  const ticket = data.ticket || null;
  const message =
    data?.error?.message ||
    err?.message ||
    'Cette addition a été modifiée sur un autre terminal. Actualisez-la avant d’enregistrer ou d’encaisser.';
  return {
    message,
    ticket,
    currentPosRevision: data.currentPosRevision ?? ticket?.posRevision ?? null,
    expectedPosRevision: data.expectedPosRevision ?? null,
    localDraftPreserved: true,
  };
}

/**
 * Défaut historique : panier local ≠ total serveur si sync omise.
 * @returns {{ divergent: boolean, localTotal: number, serverTotal: number }}
 */
export function detectTicketCartDivergence(localTotal, serverTotal) {
  const local = Math.round((Number(localTotal) || 0) * 100) / 100;
  const server = Math.round((Number(serverTotal) || 0) * 100) / 100;
  return {
    divergent: Math.abs(local - server) >= 0.005,
    localTotal: local,
    serverTotal: server,
  };
}

/**
 * Invariant L1-T1 : pas d'encaissement sans persistance confirmée.
 */
/**
 * Sync PUT ticket requis uniquement si le panier local a changé et que l’API autorise l’édition.
 */
export function shouldSyncTicketBeforePay({
  ticketPanierDirty,
  posCapabilities,
  ticketDejaPartiel,
}) {
  if (ticketDejaPartiel) return false;
  if (!ticketPanierDirty) return false;
  if (posCapabilities && posCapabilities.canEditLines === false) return false;
  return true;
}

export function resolveTicketSyncBeforePayGate({
  syncSucceeded,
  serverTotal,
  localTotal,
  syncErrorMessage,
}) {
  if (!syncSucceeded) {
    return {
      action: 'block',
      message:
        syncErrorMessage ||
        'Impossible d’enregistrer le ticket modifié. Le paiement est bloqué — réessayez ou reprenez le ticket.',
    };
  }
  if (serverTotal == null || Number.isNaN(Number(serverTotal))) {
    return {
      action: 'block',
      message: 'Total serveur indisponible après enregistrement. Paiement bloqué.',
    };
  }
  const div = detectTicketCartDivergence(localTotal, serverTotal);
  if (div.divergent) {
    return {
      action: 'block',
      message: `Le total affiché (${div.localTotal.toFixed(2)} €) ne correspond pas au ticket enregistré (${div.serverTotal.toFixed(2)} €). Rouvrez le paiement.`,
      serverTotal: div.serverTotal,
    };
  }
  return { action: 'allow', serverTotal: div.serverTotal };
}

/**
 * Mappe une ligne ticket serveur vers une ligne panier caisse (options préservées).
 */
export function mapTicketProduitToPanierLigne(lp) {
  const options = parseOptionsPayload(lp?.options);
  const produitId = lp?.produitId;
  const prepLine = lp?.posPrepLine;
  const sent = prepLine && prepLine.status !== 'CANCELLED';
  const intent = lp?.prepReleaseIntent === 'DEFER' ? 'DEFER' : 'NOW';
  return {
    lineKey: buildLineKey(produitId, options),
    commandeProduitId: lp?.id || null,
    lineType: 'produit',
    produitId,
    nom: lp?.produit?.nom || 'Produit',
    prixUnitaire: lp?.prixUnitaire,
    quantite: lp?.quantite,
    options,
    optionsLabel:
      (lp?.commentaire && String(lp.commentaire).trim()) ||
      formatOptionsSummary(options) ||
      '',
    poidsSouhaite: lp?.poidsSouhaite,
    typeDecoupe: lp?.typeDecoupe,
    prepReleaseIntent: lp?.prepReleaseIntent || 'NOW',
    prepLineStatus: prepLine?.status || null,
    requiresPreparation: lp?.produit?.requiresPreparation,
    preparationStationId: lp?.produit?.preparationStationId,
    prepSelectedForSend: sent ? false : intent !== 'DEFER',
  };
}
